import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Markdown
} from 'design-system';

import { captureEvent } from '@/services/analytics';
import { clock } from '@/services/clock';

import { feedArticleOptions } from './feedArticle.options';
import { FeedArticleDialogSkeleton } from './FeedArticleDialogSkeleton';

type FeedArticleDialogProps = {
  open: boolean;
  CTAValue: string;
  finalFocusRef?: RefObject<HTMLButtonElement | null>;
  onClose: () => void;
};

export const FeedArticleDialog = ({
  open,
  CTAValue,
  finalFocusRef,
  onClose
}: FeedArticleDialogProps) => {
  const [groupName, fileName] = CTAValue.split('/');
  const contentReadyAt = useRef<number | null>(null);
  const { data, isLoading, error } = useQuery(feedArticleOptions.file(groupName, fileName, open));

  useEffect(() => {
    if (open && !isLoading && !error && data) {
      contentReadyAt.current = clock.now();
    }
  }, [open, isLoading, error, data]);

  const handleClose = () => {
    if (contentReadyAt.current !== null) {
      captureEvent('feed_article_reading_time', {
        file_name: fileName,
        group_name: groupName,
        reading_time_seconds: Math.round((clock.now() - contentReadyAt.current) / 1000)
      });
      contentReadyAt.current = null;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={nextOpen => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent
        className='sm:max-w-[800px]'
        finalFocus={finalFocusRef}
      >
        <DialogHeader>
          <DialogTitle>Market feed article</DialogTitle>
          <DialogDescription>Detailed market commentary and analysis.</DialogDescription>
        </DialogHeader>

        {isLoading ? <FeedArticleDialogSkeleton /> : null}
        {error ? (
          <Alert>
            <AlertTitle>"error"</AlertTitle>
            <AlertDescription>The article could not be loaded. Try again shortly.</AlertDescription>
          </Alert>
        ) : null}
        {!isLoading && !error && data ? (
          <div className='max-h-[70vh] overflow-y-auto break-words text-sm'>
            <Markdown>{data}</Markdown>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
